<template>
  <main
    v-if="page"
    v-style:bg="page.backgroundColor"
    v-bind="isPreview ? { key: previewTimestamp } : null"
    :class="page.padding && [config.padding[page.padding]]"
    :data-cm-metadata="pageContentMetadata"
  >
    <div class="mr-2">
      <slot name="language-switcher" />
    </div>
    <cms-layout-article v-if="page.templateName === 'article'" :page>
      <cms-section
        v-for="section in page?.sections"
        :key="section.name"
        page-context
        :section
      />
    </cms-layout-article>

    <cms-layout-article-v2 v-else-if="page.templateName === 'article-v2'" :page>
      <cms-section
        v-for="section in page?.sections"
        :key="section.name"
        page-context
        :section
      />
    </cms-layout-article-v2>

    <cms-section
      v-else
      v-for="(section, _, i) in page?.sections"
      :key="section.name"
      :first-section="i === 0"
      page-context
      :section
    />
  </main>
</template>

<script lang="ts" setup>
import type { PageContent } from '#types/page'

definePageMeta({
  validate({ path }) {
    return !/\.\w{2,5}$/.test(path)
  },
  async middleware(to) {
    const { getPage, getSeoMetadata } = useCms(to)
    const cmsPageState = useState<PageContent>('cmsPage')
    const pageError = useState<any>('cmsPageError')
    try {
      pageError.value = null
      const page = await getPage(to.params.slug)

      if (page) {
        const { metadata, link } = getSeoMetadata(page)
        useSeoMeta(metadata)
        useHead(() => ({ link }))
        cmsPageState.value = page
        to.meta.header = {
          transparent: page.overlayHeader,
          mode: page.headerStyle || 'light'
        }
      }
    }
    catch (err) {
      pageError.value = err
    }
  }
})

const page = useState<PageContent>('cmsPage')
const pageError = useState<PageContent>('cmsPageError')

const route = useRoute()
const config = useAppConfig().pages.cms
const slug = useRouteParams<string>('slug')
const { $gtm, $optimizely } = useNuxtApp()
const cart = useCartStore()
const { trackEvent } = useOptimizely()
const { getUserEventData } = useAnalytics()
const { getPage, isPreview, triggerPreviewUpdateIndicator } = useCms(route)
const previewTimestamp = ref(Date.now())

const pageContentMetadata = computed(() => {
  if (!page.value) return null
  return JSON.stringify([{
    _: { $Ref: `content/${page.value.id}` },
    id: 'CM_METADATA_PAGE_ROOT',
    parentId: 'root',
    children: []
  }])
})

// defines markup via unhead-schema for search functionality on homepage
// https://unhead.unjs.io/schema-org/getting-started/how-it-works
useSchemaOrg([
  defineWebSite({
    potentialAction: [
      defineSearchAction({
        target: '/search?q={search_term_string}'
      })
    ]
  })
])

watch(pageError, () => {
  if (pageError.value)
    throw createError({ ...pageError.value, fatal: true })
}, { immediate: true })

useEventListener('message', async ({ data, origin }: MessageEvent) => {
  if (![origin, '*'].includes(window.com_coremedia_pbe_studioUrl) || data.type !== 'updatePreview') return
  triggerPreviewUpdateIndicator()
  page.value = (await getPage(slug.value || ''))!
  previewTimestamp.value = Date.now()
})

onMounted(async () => {
  $gtm.queueEvent('page.onLoadPageData', route, {
    pageTitle: await getPageTitle(),
    pageCategory: slug.value ? 'Content' : 'Home',
    pageName: slug.value ? page.value?.name : 'Home'
  })

  const userEventData = await getUserEventData()
  $gtm.queueEvent('user.onLoadUserData', userEventData)
  $gtm.queueEvent('page.onRouterChange', route)
  $gtm.queueEvent('cart.onCartLoad', cart)
  $gtm.flushQueue()
  $optimizely?.dataReady(userEventData)
  trackEvent('page_view', { URL: route.fullPath })
})
</script>
